import { IconName, IconSize, IconColor } from './IconLibrary';

// Icon categories for better organization
export const ICON_CATEGORIES = {
  ACTIONS: [
    'check', 'check-circle', 'delete', 'edit', 'save', 'download', 'upload',
    'close', 'cancel', 'plus', 'minus', 'trash', 'trash-alt', 'pencil',
    'settings', 'logout', 'menu'
  ] as const,
  
  NAVIGATION: [
    'chevron-down', 'chevron-up', 'chevron-right', 'arrow-down', 'arrow-up',
    'caret-down', 'caret-up', 'external-link', 'link', 'home', 'project', 'projects'
  ] as const,
  
  STATUS: [
    'warning', 'warning-filled', 'exclamation', 'exclamation-circle',
    'information', 'question', 'success', 'error', 'danger'
  ] as const,
  
  UI: [
    'three-dots', 'maximize', 'minimize', 'email', 'chat', 'chat-outline',
    'hand-pointer', 'project-diagram', 'open-full'
  ] as const,
} as const;

// Icon size presets
export const ICON_SIZES: Record<IconSize, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
  '2xl': 48,
};

// Icon color presets
export const ICON_COLORS: Record<IconColor, string> = {
  primary: '#007bff',
  secondary: '#6c757d',
  success: '#28a745',
  warning: '#ffc107',
  danger: '#dc3545',
  info: '#17a2b8',
  light: '#f8f9fa',
  dark: '#343a40',
  white: '#ffffff',
  black: '#000000',
  gray: '#6c757d',
  red: '#dc3545',
  green: '#28a745',
  blue: '#007bff',
  yellow: '#ffc107',
  orange: '#fd7e14',
  inherit: 'inherit',
  current: 'currentColor',
};

// Common icon combinations for specific use cases
export const ICON_PRESETS = {
  // Button icons
  BUTTON: {
    primary: { size: 'sm' as IconSize, color: 'white' as IconColor },
    secondary: { size: 'sm' as IconSize, color: 'current' as IconColor },
    danger: { size: 'sm' as IconSize, color: 'white' as IconColor },
  },
  
  // Status indicators
  STATUS: {
    success: { size: 'md' as IconSize, color: 'success' as IconColor },
    warning: { size: 'md' as IconSize, color: 'warning' as IconColor },
    error: { size: 'md' as IconSize, color: 'danger' as IconColor },
    info: { size: 'md' as IconSize, color: 'info' as IconColor },
  },
  
  // Navigation
  NAVIGATION: {
    default: { size: 'sm' as IconSize, color: 'current' as IconColor },
    large: { size: 'md' as IconSize, color: 'current' as IconColor },
  },
  
  // Actions
  ACTION: {
    small: { size: 'sm' as IconSize, color: 'current' as IconColor },
    medium: { size: 'md' as IconSize, color: 'current' as IconColor },
    large: { size: 'lg' as IconSize, color: 'current' as IconColor },
  },
} as const;

// Utility functions
export const getIconSize = (size: IconSize | number): number => {
  return typeof size === 'number' ? size : ICON_SIZES[size];
};

export const getIconColor = (color: IconColor | string): string => {
  return ICON_COLORS[color as IconColor] || color;
};

export const isIconName = (name: string): name is IconName => {
  return Object.values(ICON_CATEGORIES).flat().includes(name as any);
};

export const getIconsByCategory = (category: keyof typeof ICON_CATEGORIES): IconName[] => {
  return [...ICON_CATEGORIES[category]] as IconName[];
};

export const getAllIconNames = (): IconName[] => {
  return Object.values(ICON_CATEGORIES).flat() as IconName[];
};

// Icon mapping for common patterns
export const ICON_MAPPINGS = {
  // Check variations
  CHECK_VARIANTS: ['check', 'check-circle'] as const,
  
  // Arrow variations
  ARROW_VARIANTS: ['chevron-down', 'chevron-up', 'chevron-right', 'arrow-down', 'arrow-up'] as const,
  
  // Delete variations
  DELETE_VARIANTS: ['delete', 'trash', 'trash-alt'] as const,
  
  // Edit variations
  EDIT_VARIANTS: ['edit', 'pencil'] as const,
  
  // Close variations
  CLOSE_VARIANTS: ['close', 'cancel'] as const,
} as const;

// Helper function to get the best icon for a specific use case
export const getBestIconForUseCase = (useCase: string): IconName | null => {
  const useCaseMap: Record<string, IconName> = {
    // Check/validation
    'check': 'check',
    'validation': 'check-circle',
    'success': 'check-circle',
    'completed': 'check-circle',
    
    // Actions
    'delete': 'delete',
    'remove': 'trash',
    'edit': 'edit',
    'modify': 'pencil',
    'save': 'save',
    'download': 'download',
    'upload': 'upload',
    'close': 'close',
    'cancel': 'cancel',
    'add': 'plus',
    'subtract': 'minus',
    
    // Navigation
    'expand': 'chevron-down',
    'collapse': 'chevron-up',
    'next': 'chevron-right',
    'down': 'arrow-down',
    'up': 'arrow-up',
    'external': 'external-link',
    'link': 'link',
    'home': 'home',
    'project': 'project',
    
    // Status
    'warning': 'warning',
    'error': 'error',
    'danger': 'danger',
    'info': 'information',
    'question': 'question',
    'help': 'question',
    
    // UI
    'menu': 'three-dots',
    'more': 'three-dots',
    'options': 'three-dots',
    'maximize': 'maximize',
    'minimize': 'minimize',
    'email': 'email',
    'chat': 'chat',
    'pointer': 'hand-pointer',
  };
  
  return useCaseMap[useCase.toLowerCase()] || null;
};

// Helper function to get icon with preset styling
export const getIconWithPreset = (
  name: IconName,
  preset: keyof typeof ICON_PRESETS,
  variant?: keyof typeof ICON_PRESETS[typeof preset]
) => {
  const presetConfig = ICON_PRESETS[preset];
  const variantConfig = variant ? presetConfig[variant] : Object.values(presetConfig)[0];
  
  return {
    name,
    ...variantConfig,
  };
}; 